import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { getPurchaseMetrics } from '../api/client'
import { currentLocale } from '../i18n'
import PageHeader from '../components/PageHeader'

// Chip colors for the attention table (soft tints).
const STATE_COLOR = {
  approved: 'bg-blue-100 text-blue-700',
  sap:      'bg-violet-100 text-violet-700',
  ordered:  'bg-indigo-100 text-indigo-700',
  received: 'bg-emerald-100 text-emerald-700',
  closed:   'bg-gray-100 text-gray-500',
}

// The pipeline, in flow order. Solid fills drive the proportion bar; the dot
// echoes the fill in the legend.
const PIPELINE = [
  { key: 'approved', fill: 'bg-blue-500',    dot: 'bg-blue-500' },
  { key: 'sap',      fill: 'bg-violet-500',  dot: 'bg-violet-500' },
  { key: 'ordered',  fill: 'bg-indigo-500',  dot: 'bg-indigo-500' },
  { key: 'received', fill: 'bg-emerald-500', dot: 'bg-emerald-500' },
]

// Full precision, for per-item values and hover titles: "32.942.980 CLP".
function fmtMoney(value, currency) {
  if (value == null) return '—'
  return `${Math.round(value).toLocaleString(currentLocale())} ${currency}`
}

// Compact, for aggregate figures that would otherwise pile up: "33.2M CLP".
function fmtCompact(value, currency) {
  if (value == null) return '—'
  const s = new Intl.NumberFormat(currentLocale(), { notation: 'compact', maximumFractionDigits: 1 }).format(Number(value))
  return `${s} ${currency}`
}

function fmtDate(iso) {
  if (!iso) return '—'
  const d = new Date(iso + 'T00:00:00')
  return d.toLocaleDateString(currentLocale(), { month: 'short', day: 'numeric' })
}

function dayDelta(iso) {
  if (!iso) return null
  const d = new Date(iso + 'T00:00:00')
  return Math.round((d - new Date().setHours(0, 0, 0, 0)) / 86400000)
}

// Late first (most overdue first), then soonest needed-by, then undated.
function buildAttention(lateItems, queue) {
  const byId = new Map()
  const all = [
    ...(lateItems || []),
    ...(queue?.approved_to_sap || []),
    ...(queue?.sap_awaiting_order || []),
    ...(queue?.ordered_awaiting_receipt || []),
  ]
  for (const r of all) {
    if (!byId.has(r.id)) byId.set(r.id, r)
  }
  const rank = (r) => {
    const d = dayDelta(r.needed_by)
    if (d == null) return 2
    return d < 0 ? 0 : 1
  }
  return [...byId.values()].sort((a, b) => {
    const ra = rank(a), rb = rank(b)
    if (ra !== rb) return ra - rb
    const da = dayDelta(a.needed_by), dbb = dayDelta(b.needed_by)
    if (da == null && dbb == null) return 0
    if (da == null) return 1
    if (dbb == null) return -1
    return da - dbb
  }).slice(0, 8)
}

export default function PurchasesDashboard() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const { t } = useTranslation('purchases')

  useEffect(() => {
    let mounted = true
    setLoading(true)
    getPurchaseMetrics()
      .then(d => { if (mounted) setData(d) })
      .catch(e => { if (mounted) setError(e.response?.data?.detail || 'Failed to load') })
      .finally(() => { if (mounted) setLoading(false) })
    return () => { mounted = false }
  }, [])

  if (loading) return <div className="p-8 text-sm text-gray-400">Loading…</div>
  if (error)   return <div className="p-8 text-sm text-red-500">{error}</div>
  if (!data)   return null

  const {
    my = {}, team = {}, queue = {}, late_items = [],
    spend_30d = [], received_30d_count = 0, top_projects = [],
  } = data

  const attention = buildAttention(late_items, queue)
  const lateCount = late_items.length

  return (
    <div className="p-8 space-y-6">
      <PageHeader title={t('dashboard.title')} subtitle={t('dashboard.subtitle')} />

      {/* Signature: the pipeline as one proportion bar — volume is visible, not counted */}
      <PipelineBar byState={team.by_state || {}} t={t} />

      {/* Hero queue on the left, a quiet metrics rail on the right */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        <div className="lg:col-span-2">
          <AttentionTable items={attention} t={t} />
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <MiniStat value={team.active_count ?? 0} label={t('dashboard.kpi.openRequests')}
                      sub={t('dashboard.kpi.mine', { count: my.active_count ?? 0 })} />
            <MiniStat value={lateCount} label={t('dashboard.kpi.late')}
                      accent={lateCount ? 'text-red-600' : 'text-emerald-600'}
                      sub={lateCount ? t('dashboard.kpi.pastNeededBy') : t('dashboard.kpi.allOnSchedule')} />
            <MiniStat value={received_30d_count} label={t('dashboard.kpi.received30d')}
                      sub={t('dashboard.kpi.completed')} />
          </div>

          <TopProjects items={top_projects} t={t} />

          <RailCard title={t('dashboard.spend.title')} note={t('dashboard.spend.subtitle')}>
            <CurrencyStack list={spend_30d} showCount t={t} empty={t('dashboard.spend.empty')} />
          </RailCard>
        </div>
      </div>
    </div>
  )
}

// ── helpers ────────────────────────────────────────────────────────────────

function PipelineBar({ byState, t }) {
  const steps = PIPELINE.map(s => ({ ...s, count: byState[s.key] || 0 }))
  const total = steps.reduce((a, s) => a + s.count, 0)

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-900">{t('dashboard.pipeline.title')}</h3>
        <span className="text-xs text-gray-400">{t('dashboard.pipeline.openTotal', { count: total })}</span>
      </div>

      {total === 0 ? (
        <div className="h-11 rounded-lg bg-gray-50 border border-dashed border-gray-200 flex items-center justify-center text-xs text-gray-400">
          {t('dashboard.pipeline.empty')}
        </div>
      ) : (
        <div className="flex h-11 gap-1">
          {steps.filter(s => s.count > 0).map(s => {
            const pct = (s.count / total) * 100
            return (
              <Link
                key={s.key}
                to="/purchases"
                title={`${s.count} · ${t(`dashboard.pipeline.${s.key}`)}`}
                className={`${s.fill} first:rounded-l-lg last:rounded-r-lg flex items-center justify-center text-white text-sm font-bold tabular-nums hover:opacity-90 transition-opacity`}
                style={{ width: `${pct}%`, minWidth: '2.75rem' }}
              >
                {pct > 6 ? s.count : ''}
              </Link>
            )
          })}
        </div>
      )}

      <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5">
        {steps.map(s => (
          <div key={s.key} className="flex items-center gap-1.5 text-xs">
            <span className={`w-2 h-2 rounded-full ${s.dot} ${s.count === 0 ? 'opacity-30' : ''}`} />
            <span className="font-semibold text-gray-700 tabular-nums">{s.count}</span>
            <span className="text-gray-400">{t(`dashboard.pipeline.${s.key}`)}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

function MiniStat({ label, value, sub, accent = 'text-gray-900' }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-3.5">
      <div className={`text-2xl font-bold tabular-nums leading-none ${accent}`}>{value}</div>
      <div className="mt-1.5 text-[11px] uppercase tracking-wide text-gray-400 font-medium leading-tight">{label}</div>
      {sub && <div className="mt-0.5 text-[11px] text-gray-400 leading-tight truncate" title={sub}>{sub}</div>}
    </div>
  )
}

function RailCard({ title, note, children }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <div className="text-[11px] uppercase tracking-wide text-gray-400 font-medium">{title}</div>
      {note && <div className="text-[11px] text-gray-400 mb-2.5">{note}</div>}
      {!note && <div className="mb-2.5" />}
      {children}
    </div>
  )
}

const CCY_SYMBOL = { EUR: '€', USD: '$', GBP: '£', BRL: 'R$', CNY: '¥', AUD: 'A$' }

// Top spending projects (30d), ranked by CLP with a proportional bar. Foreign
// currencies don't feed the ranking (no FX) but are flagged with a marker.
function TopProjects({ items, t }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <div className="text-[11px] uppercase tracking-wide text-gray-400 font-medium">{t('dashboard.topProjects.title')}</div>
      <div className="text-[11px] text-gray-400 mb-3">{t('dashboard.topProjects.subtitle')}</div>
      {(!items || items.length === 0) ? (
        <div className="text-sm text-gray-400">{t('dashboard.spend.empty')}</div>
      ) : (
        <ul className="space-y-2.5">
          {items.map((p, i) => {
            const maxClp = Math.max(...items.map(x => x.clp), 1)
            const useClp = p.clp > 0 || !p.foreign?.length
            const primary = useClp
              ? fmtCompact(p.clp, 'CLP')
              : fmtCompact(p.foreign[0].value, p.foreign[0].currency)
            const markers = (useClp ? (p.foreign || []) : p.foreign.slice(1))
              .map(f => CCY_SYMBOL[f.currency] || f.currency)
            const pct = Math.round((p.clp / maxClp) * 100)
            const title = [fmtMoney(p.clp, 'CLP'), ...(p.foreign || []).map(f => fmtMoney(f.value, f.currency))].join('  ·  ')
            return (
              <li key={`${p.project}-${i}`}>
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-xs text-gray-700 truncate" title={p.project}>{p.project}</span>
                  <span className="text-xs font-semibold text-gray-900 tabular-nums whitespace-nowrap" title={title}>
                    {primary}
                    {markers.length > 0 && <span className="text-gray-400 font-normal"> +{markers.join('')}</span>}
                  </span>
                </div>
                <div className="mt-1 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                  <div className="h-full bg-gray-700 rounded-full" style={{ width: `${pct}%` }} />
                </div>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

// Each currency on its own line, dominant first — never concatenated inline.
function CurrencyStack({ list, showCount = false, empty, t }) {
  if (!list || list.length === 0) {
    return <div className="text-sm text-gray-400">{empty || '—'}</div>
  }
  const sorted = [...list].sort((a, b) => b.value - a.value)
  return (
    <ul className="space-y-1.5">
      {sorted.map(s => (
        <li key={s.currency} className="flex items-baseline justify-between gap-3">
          <span className="text-lg font-bold text-gray-900 tabular-nums leading-none" title={fmtMoney(s.value, s.currency)}>
            {fmtCompact(s.value, s.currency)}
          </span>
          {showCount && s.count != null && (
            <span className="text-[11px] text-gray-400 whitespace-nowrap">
              {t(s.count === 1 ? 'dashboard.spend.orderCount_one' : 'dashboard.spend.orderCount_other', { count: s.count })}
            </span>
          )}
        </li>
      ))}
    </ul>
  )
}

function AttentionTable({ items, t }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100">
        <h3 className="text-sm font-semibold text-gray-900">{t('dashboard.attention.title')}</h3>
        <p className="text-[11px] text-gray-400">{t('dashboard.attention.subtitle')}</p>
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-gray-400 px-4 py-12 text-center">{t('dashboard.attention.empty')}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-[11px] uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-2 font-semibold">{t('dashboard.attention.cols.item')}</th>
                <th className="text-left px-3 py-2 font-semibold">{t('dashboard.attention.cols.state')}</th>
                <th className="text-left px-3 py-2 font-semibold">{t('dashboard.attention.cols.neededBy')}</th>
                <th className="text-right px-4 py-2 font-semibold">{t('dashboard.attention.cols.value')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {items.map(r => {
                const delta = dayDelta(r.needed_by)
                const dateColor = delta == null ? 'text-gray-400'
                  : delta < 0 ? 'text-red-600'
                  : delta <= 7 ? 'text-amber-600' : 'text-gray-500'
                const sub = [r.requester_name, r.supplier].filter(Boolean).join(' · ')
                return (
                  <tr key={r.id} className="hover:bg-gray-50/60">
                    <td className="px-4 py-2.5 max-w-0">
                      <div className="flex items-center gap-2">
                        <Link to={`/purchases/${r.id}`} className="font-medium text-gray-900 hover:underline truncate">
                          {r.item_description || <span className="italic text-gray-500">{t('dashboard.multiItem')}</span>}
                        </Link>
                        {r.is_mine && <span className="shrink-0 text-[10px] uppercase tracking-wide bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">{t('dashboard.attention.mineTag')}</span>}
                      </div>
                      <div className="text-[11px] text-gray-400 truncate">{sub || '—'}</div>
                    </td>
                    <td className="px-3 py-2.5">
                      <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${STATE_COLOR[r.state] || 'bg-gray-100 text-gray-500'}`}>
                        {t(`states.${r.state}`) || r.state}
                      </span>
                    </td>
                    <td className={`px-3 py-2.5 font-medium tabular-nums whitespace-nowrap ${dateColor}`}>
                      {r.needed_by
                        ? <>{fmtDate(r.needed_by)}{delta != null && delta < 0 && <span className="text-[10px]"> {t('dashboard.attention.daysLate', { count: -delta })}</span>}</>
                        : '—'}
                    </td>
                    <td className="px-4 py-2.5 text-right text-gray-700 tabular-nums whitespace-nowrap" title={r.value > 0 ? fmtMoney(r.value, r.currency) : ''}>
                      {r.value > 0 ? fmtMoney(r.value, r.currency) : '—'}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
